/**
 * Invite-staff sheet for Settings > My-Kitchen (Plan 19).
 *
 * Collects: full name, email, phone (REQUIRED), role (CHEF_ADMIN or
 * CHEF_STAFF). Submitting creates an `Invitation(kind: CHEF_STAFF)`
 * row — the Plan-36 InvitationDispatcher cron picks it up and sends the
 * Boombox magic-link.
 *
 * Phone is required (per the plan's mutation comment) because kitchen
 * staff rely on SMS for shipment events + same-day operations comms.
 * Only STUDENT ProfileCode allows phone-null at create-time anywhere
 * in the platform.
 */

import { FormField } from '@ordo/ui/form-field'
import { FormSheet } from '@ordo/ui/form-sheet'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { toast } from '@ordo/ui/sonner'
import { useEffect, useState } from 'react'

import { type ChefRole, isValidChefStaffPhone, useInviteChefStaffMutation } from '@/api/kitchen-settings'

export interface InviteStaffSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  supplier?: boolean
  kitchenOrgId: string
}

const ROLE_OPTIONS: Array<{ value: ChefRole; label: string; description: string }> = [
  {
    value: 'CHEF_STAFF',
    label: 'Staff',
    description: 'Production tasks — kitchen view, shipment receive, the line. Most invitations are staff.',
  },
  {
    value: 'CHEF_ADMIN',
    label: 'Admin',
    description: 'Full kitchen management — settings, payouts, staff onboarding. Use sparingly.',
  },
]

export function InviteStaffSheet({ open, onOpenChange, kitchenOrgId, supplier = false }: InviteStaffSheetProps) {
  const roleOptions = supplier
    ? ROLE_OPTIONS.map((option) => ({
        ...option,
        value: option.value.replace('CHEF_', 'SUPPLIER_') as ChefRole,
        label: option.label.replace(/Chef/g, 'Supplier'),
        description: option.description.replace(/kitchen/g, 'supplier business'),
      }))
    : ROLE_OPTIONS
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState<ChefRole>(supplier ? 'SUPPLIER_STAFF' : 'CHEF_STAFF')
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const mutation = useInviteChefStaffMutation()

  // Reset on open — a stale draft after a successful submit shouldn't
  // leak back into the next invite.
  useEffect(() => {
    if (open) {
      setName('')
      setEmail('')
      setPhone('')
      setRole(supplier ? 'SUPPLIER_STAFF' : 'CHEF_STAFF')
      setPhoneError(null)
    }
  }, [open, supplier])

  async function handleSubmit() {
    const trimmedName = name.trim()
    const trimmedEmail = email.trim()
    const trimmedPhone = phone.trim()
    if (!trimmedName || !trimmedEmail || !trimmedPhone) {
      throw new Error('Name, email, and phone are all required.')
    }
    if (!isValidChefStaffPhone(trimmedPhone)) {
      setPhoneError('Phone must be 6-20 chars of digits / spaces / dashes / parens / +')
      throw new Error('Invalid phone')
    }
    try {
      const result = await mutation.mutateAsync({
        kitchenOrgId,
        email: trimmedEmail,
        name: trimmedName,
        phone: trimmedPhone,
        role,
      })
      if (result.existingMembershipId) {
        toast.info('Already a team member', {
          description: `${trimmedEmail} already has access to this kitchen.`,
        })
      } else {
        toast.success('Invitation queued', {
          description: `${trimmedEmail} will receive a magic-link by email + SMS.`,
        })
      }
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      toast.error("Couldn't send invitation", { description: message })
      throw new Error(message, { cause: err })
    }
  }

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Invite staff"
      description="They'll receive a magic-link by email + SMS. The link expires in 14 days."
      submitLabel="Send invitation"
      onSubmit={handleSubmit}
      size="md">
      <FormField label="Full name" htmlFor="invite-name" required>
        <Input
          id="invite-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Doe"
          required
        />
      </FormField>

      <FormField label="Email" htmlFor="invite-email" required>
        <Input
          id="invite-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jane@kitchen.com"
          required
        />
      </FormField>

      <FormField
        label="Phone (required)"
        htmlFor="invite-phone"
        required
        error={phoneError ?? undefined}
        helperText="We text shipment events; line staff rely on this.">
        <Input
          id="invite-phone"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value)
            if (phoneError) {
              setPhoneError(null)
            }
          }}
          placeholder="(555) 555-0100"
          required
        />
      </FormField>

      <FormField label="Role">
        <RadioGroup value={role} onValueChange={(v) => setRole(v as ChefRole)} className="flex flex-col gap-2">
          {roleOptions.map((opt) => (
            <Label
              key={opt.value}
              htmlFor={`invite-role-${opt.value}`}
              className="flex cursor-pointer items-start gap-3 rounded-md border bg-card p-3 hover:bg-accent/50">
              <RadioGroupItem id={`invite-role-${opt.value}`} value={opt.value} className="mt-0.5" />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{opt.label}</span>
                <span className="text-xs text-muted-foreground">{opt.description}</span>
              </div>
            </Label>
          ))}
        </RadioGroup>
      </FormField>
    </FormSheet>
  )
}
